import React, { useState, useEffect } from "react";
import {
  discountOffersList,
  discountOffersDelete,
  discountOffersStatusUpdate,
} from "../../../api/DiscountOffers";
import { batchList } from "../../../api/Batch";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../../master/Master";
import usePrivateLink from "../../config/usePrivateLink";
import AcademicPortalLayout from "../AcademicPortalLayout";

export default function DiscountOffersList() {
  const hasAccess = usePrivateLink();
  const [offers, setOffers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    session_id: "",
    batch_id: "",
    search: "",
    status: "",
  });

  const offersFetch = async () => {
    setLoading(true);

    let addUrl = "";
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        addUrl += `&${key}=${encodeURIComponent(filters[key])}`;
      }
    });

    try {
      const response = await discountOffersList(addUrl);
      if (response.data.status) {
        setOffers(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const batchFetch = async () => {
    try {
       const response = await batchList(`session_id=${filters?.session_id}`);
      if (response.data.status) {
        setBatches(response?.data?.data);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    }
  };

    useEffect(() => {
      batchFetch();
    }, [filters?.session_id]);

  const handleOfferDelete = async (id) => {
    setLoading(true);
    try {
      const response = await discountOffersDelete(id);
      if (response.data.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      offersFetch();
    }
  };

  const handleStatusToggle = async (id) => {
    setLoading(true);
    try {
      const response = await discountOffersStatusUpdate(id);
      if (response.data.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      offersFetch();
    }
  };

  const formatDiscount = (offer) => {
    if (
      offer.discount_type === "fixed" ||
      (!offer.discount_type && offer.discount_amount)
    ) {
      return `₹${Number(offer.discount_amount || 0).toLocaleString("en-IN")}`;
    }
    return `${offer.discount_percentage || 0}%`;
  };

  const formatEligibility = (offer) => {
    if (offer.min_percentage || offer.max_percentage) {
      return `${offer.min_percentage || 0}% - ${offer.max_percentage || 0}%`;
    }
    return "N/A";
  };

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);

    const activeSession = parseSessions.find((s) => String(s.status) === "1");
    setFilters((prev) => ({
      ...prev,
      session_id: activeSession?.id || "",
    }));

    batchFetch();
  }, []);

  useEffect(() => {
    offersFetch();
  }, [filters]);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <AcademicPortalLayout />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-2">
                          <div className="col-sm-6">
                            <h4 className="text-primary m-0">
                              Discount Offers
                            </h4>
                          </div>
                          <div className="col-sm-6 text-end">
                            {hasAccess("/discount/offers/add") && (
                              <Link
                                to={"/discount/offers/add"}
                                className="btn btn-outline-primary btn-sm"
                              >
                                <i className="ri-add-circle-line me-2"></i>
                                Add Discount Offer
                              </Link>
                            )}
                          </div>

                          <div className="col-12">
                            <div className="row g-3 mt-1">
                              <div className="col-md-3 form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={filters.session_id}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      session_id: e.target.value,
                                      batch_id: "",
                                    })
                                  }
                                >
                                  <option value="">All</option>
                                  {sessions.map((session) => (
                                    <option key={session.id} value={session.id}>
                                      {session.session} :{" "}
                                      {session.status === "1"
                                        ? "Active"
                                        : "Inactive"}
                                    </option>
                                  ))}
                                </select>
                                <label>Filter by Session</label>
                              </div>

                              <div className="col-md-3 form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={filters.batch_id}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      batch_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">All Batches</option>
                                  {batches
                                    .filter(
                                      (batch) =>
                                        !filters.session_id ||
                                        batch.session_id == filters.session_id,
                                    )
                                    .map((batch) => (
                                      <option key={batch.id} value={batch.id}>
                                        {batch.title}
                                      </option>
                                    ))}
                                </select>
                                <label>Filter by Batch</label>
                              </div>

                              <div className="col-md-3 form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search"
                                  value={filters.search}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      search: e.target.value,
                                    })
                                  }
                                />
                                <label>Search Offer</label>
                              </div>

                              <div className="col-md-3 d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className={`btn ${
                                    filters.status === "1"
                                      ? "btn-success"
                                      : "btn-outline-success"
                                  }`}
                                  onClick={() =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      status: "1",
                                    }))
                                  }
                                >
                                  Active
                                </button>
                                <button
                                  type="button"
                                  className={`btn ${
                                    filters.status === "2"
                                      ? "btn-danger"
                                      : "btn-outline-danger"
                                  }`}
                                  onClick={() =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      status: "2",
                                    }))
                                  }
                                >
                                  Inactive
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="table-responsive">
                              <table className="table table-hover">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Title</th>
                                    <th>Batch</th>
                                    <th>Eligibility</th>
                                    <th>Discount</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {offers.length === 0 ? (
                                    <tr>
                                      <td colSpan="8">No offers available</td>
                                    </tr>
                                  ) : (
                                    offers.map((item, index) => (
                                      <tr key={item.id}>
                                        <td>{index + 1 || "N/A"}</td>
                                        <td>{item?.title || "N/A"}</td>
                                        <td>{item?.batch?.title || "N/A"}</td>
                                        <td>{formatEligibility(item)}</td>
                                        <td>{formatDiscount(item)}</td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              item.status === "1"
                                                ? "bg-label-success"
                                                : "bg-label-danger"
                                            }`}
                                            role="button"
                                            onClick={() => {
                                              Swal.fire({
                                                title: "Are you sure?",
                                                text: `This offer will be marked as ${
                                                  item.status === "1"
                                                    ? "Inactive"
                                                    : "Active"
                                                }.`,
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#198754",
                                                cancelButtonColor: "#6c757d",
                                                confirmButtonText:
                                                  "Yes, update it!",
                                                cancelButtonText: "Cancel",
                                              }).then((result) => {
                                                if (result.isConfirmed) {
                                                  handleStatusToggle(item?.id);
                                                }
                                              });
                                            }}
                                          >
                                            {item.status === "1"
                                              ? "Active"
                                              : "Inactive"}
                                          </span>
                                        </td>
                                        <td>
                                          <div className="d-flex gap-2">
                                            {hasAccess(
                                              "/discount/offers/update",
                                            ) && (
                                              <Link
                                                className="btn btn-outline-info btn-sm"
                                                to={`/discount/offers/update?id=${item?.id}`}
                                              >
                                                <i className="ri-ball-pen-line"></i>
                                              </Link>
                                            )}
                                            {hasAccess(
                                              "/discount/offers/delete",
                                            ) && (
                                              <button
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => {
                                                  Swal.fire({
                                                    title: "Are you sure?",
                                                    text: "This action will permanently delete!",
                                                    icon: "warning",
                                                    showCancelButton: true,
                                                    confirmButtonColor: "#d33",
                                                    cancelButtonColor:
                                                      "#6c757d",
                                                    confirmButtonText:
                                                      "Yes, delete it!",
                                                    cancelButtonText: "Cancel",
                                                  }).then(async (result) => {
                                                    if (result.isConfirmed) {
                                                      handleOfferDelete(
                                                        item?.id,
                                                      );
                                                    }
                                                  });
                                                }}
                                              >
                                                <i className="ri-delete-bin-line"></i>
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
